'use client';

import React, { useCallback, useEffect, useMemo } from 'react';
import { myVisitCounselApi } from '@/entities/planner/model/my-planner.api';
import type { VisitCounselView } from '@/entities/visit-counsel/model/visit-counsel.types';
import type { InquiryFlowSnapshot } from '@miso/design-system';
import type {
  RecordArrivalSiteRequest,
  MovingSiteRequest,
} from '@/entities/visit-counsel/model/intake.types';
import {
  buildMovingMethodFlow,
  flowResultToMovingMethod,
  movingMethodToFlowValues,
} from '../model/buildMovingMethodFlow';
import { structuredToStartAddress } from '../model/addressAdapters';
import { createProgressFlowFetcher } from '@/shared/api/inquiry-flow/fetchProgressFlow';
import { ensureIntakeSessionStarted } from '../model/ensureIntakeSessionStarted';
import { findLastVisibleQuestionId } from '../model/findLastVisibleQuestionId';
import { StepFlow } from './StepFlow';

const FLOW_ID = 'progress-arrival-transport';

interface Props {
  visitCounselId: string;
  counsel: VisitCounselView;
  showToast: (message: string, severity?: 'success' | 'error' | 'warning' | 'neutral') => void;
  refetch: () => Promise<{ counsel?: VisitCounselView | null } | undefined>;
  onAfterDone: (counsel: VisitCounselView) => void;
  onCancel: () => void;
  /** 이전 sub-stage 로부터 뒤로가기 진입 — 마지막 visible 질문에 위치. */
  startAtLast?: boolean;
  progressOffset?: number;
  progressTotal?: number;
  onStateChange?: (snap: InquiryFlowSnapshot) => void;
}

/**
 * 도착지 운반방식 step (Figma `225:16062` 등).
 *
 * 출발지 운반방식과 동일한 분기 InquiryFlow 를 site='arrival' 로 빌드. 저장 시
 * `recordArrivalSite` 로 도착지 주소+평수 유지 + 운반방식/엘리베이터 갱신.
 */
export function ArrivalTransportStepAdapter({
  visitCounselId,
  counsel,
  showToast,
  refetch,
  onAfterDone,
  onCancel,
  startAtLast,
  progressOffset,
  progressTotal,
  onStateChange,
}: Props) {
  // Firestore `playground_flows/progress-arrival-transport` 의 FlowDocument 를 비동기 로드.
  const fetcher = useMemo(() => createProgressFlowFetcher(FLOW_ID), []);

  // 뒤로가기 진입 위치 계산용 로컬 FlowDocument — Firestore 응답을 동기로 받을 수 없으므로
  // 같은 빌더로 `findLastVisibleQuestionId` 를 평가한다. 자세한 정책은
  // DepartureTransportStepAdapter 의 동일 변수 주석 참조.
  const localFlowForBackNav = useMemo(() => buildMovingMethodFlow({ site: 'arrival' }), []);

  const initialValues = useMemo(
    () =>
      movingMethodToFlowValues(
        counsel.arrivalMovingMethod ?? null,
        counsel.arrivalHasElevator ?? null
      ),
    [counsel.arrivalMovingMethod, counsel.arrivalHasElevator]
  );

  const startAtQuestionId = useMemo(() => {
    if (!startAtLast) return undefined;
    return findLastVisibleQuestionId(localFlowForBackNav, 'transport', initialValues ?? {});
  }, [startAtLast, localFlowForBackNav, initialValues]);

  // 마운트 시점 intake-session 보장.
  useEffect(() => {
    void ensureIntakeSessionStarted(visitCounselId, counsel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleComplete = useCallback(
    async (values: Record<string, unknown>) => {
      const result = flowResultToMovingMethod(values);
      if (!result) {
        showToast('운반방식을 선택해주세요.', 'warning');
        return;
      }

      // 도착지 주소는 이미 저장되어 있다 (resolver 가 arrival-address 단계 이후 진입 보장).
      const arrAddr = counsel.arrivalAddress;
      const intakeArrival = counsel.sessions?.intake?.arrival;
      const supplyArea =
        (typeof counsel.arrivalSupplyArea === 'number' && counsel.arrivalSupplyArea > 0
          ? counsel.arrivalSupplyArea
          : null) ??
        (typeof intakeArrival?.supplyArea === 'number' && intakeArrival.supplyArea > 0
          ? intakeArrival.supplyArea
          : null) ??
        (typeof counsel.supplyArea === 'number' && counsel.supplyArea > 0
          ? counsel.supplyArea
          : null) ??
        1;

      const arrival: MovingSiteRequest = {
        supplyArea,
        address: arrAddr ? structuredToStartAddress(arrAddr) : null,
        hasElevator: result.hasElevator,
        movingMethod: result.movingMethod,
      };
      const request: RecordArrivalSiteRequest = { arrival };

      try {
        await ensureIntakeSessionStarted(visitCounselId, counsel);
        await myVisitCounselApi.recordArrivalSite(visitCounselId, request);
        showToast('도착지 운반방식이 저장되었습니다.', 'success');
        const refetched = await refetch();
        const updated = refetched?.counsel ?? null;
        if (updated) onAfterDone(updated);
        else onCancel();
      } catch (err) {
        showToast(
          err instanceof Error ? err.message : '도착지 운반방식 저장에 실패했습니다.',
          'error'
        );
      }
    },
    [visitCounselId, counsel, showToast, refetch, onAfterDone, onCancel]
  );

  return (
    <StepFlow
      fetcher={fetcher}
      initialValues={initialValues}
      onComplete={handleComplete}
      onCancel={onCancel}
      onError={(err) => showToast(err.message ?? '플로우 오류가 발생했습니다.', 'error')}
      startAtQuestionId={startAtQuestionId}
      progressOffset={progressOffset}
      progressTotal={progressTotal}
      onStateChange={onStateChange}
    />
  );
}
